import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, Alert, MenuItem, Chip, IconButton,
  Tabs, Tab, Fab, Switch, CircularProgress, Divider
} from '@mui/material';
import { Add, Edit, Delete, Settings, CalendarToday } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import api from '../services/api';
import { updateSetting } from '../store/settingsSlice';

const DropdownManagement = () => {
  const dispatch = useDispatch();
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [formData, setFormData] = useState({
    category: '',
    value: '',
    label: '',
    order: 0
  });
  const [editOption, setEditOption] = useState(null);

  const user = useSelector(state => state.auth.user);
  const { showAllDates, updating: updatingSetting } = useSelector(state => state.settings);

  const categories = [
    { value: 'departments', label: 'Departments' },
    { value: 'storageTemperature', label: 'Storage Temperature' },
    { value: 'regularity', label: 'Regularity' },
    { value: 'bisSubcategories', label: 'BIS Subcategories' },
    { value: 'fssaiCategory', label: 'FSSAI Category' }
  ];

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      const response = await api.get('/dropdowns/admin/all');
      setOptions(response.data.data);
    } catch (error) {
      setError('Failed to fetch dropdown options');
    }
  };

  const handleToggleShowAllDates = async () => {
    setError('');
    setSuccess('');
    try {
      const newStatus = !showAllDates;
      await dispatch(updateSetting({ key: 'showAllDates', value: newStatus })).unwrap();
      setSuccess(`All dates are now ${newStatus ? 'visible' : 'hidden'} across entire application and Excel export!`);
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to update date visibility setting');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (editOption) {
        await api.put(`/dropdowns/${editOption._id}`, formData);
        setSuccess('Option updated successfully!');
      } else {
        await api.post('/dropdowns', formData);
        setSuccess('Option created successfully!');
      }
      setOpen(false);
      setEditOption(null);
      setFormData({ category: '', value: '', label: '', order: 0 });
      fetchOptions();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save option');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleEdit = (option) => {
    setEditOption(option);
    setFormData({
      category: option.category,
      value: option.value,
      label: option.label,
      order: option.order
    });
    setOpen(true);
  };

  const handleToggleActive = async (optionId, currentStatus) => {
    try {
      await api.put(`/dropdowns/${optionId}`, { active: !currentStatus });
      setSuccess('Status updated successfully!');
      fetchOptions();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update status');
    }
  };

  const getFilteredOptions = () => {
    const categoryValue = categories[selectedTab]?.value;
    return options.filter(option => option.category === categoryValue);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'departments': 'primary',
      'storageTemperature': 'secondary',
      'regularity': 'info',
      'bisSubcategories': 'warning',
      'fssaiCategory': 'success'
    };
    return colors[category] || 'default';
  };

  if (user?.role !== 'Admin') {
    return (
      <Box>
        <Alert severity="error">Access denied. Only Admin can manage utility options.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Utility & Dropdown Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditOption(null);
            setFormData({ 
              category: categories[selectedTab]?.value || '', 
              value: '', 
              label: '', 
              order: 0 
            });
            setOpen(true);
          }}
        >
          Add Option
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Date Visibility Setting Control Card */}
      <Paper 
        sx={{ 
          p: { xs: 2, sm: 2.5 }, 
          mb: 3, 
          borderRadius: 2, 
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,1) 100%)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          flexDirection: { xs: 'column', sm: 'row' }, 
          gap: 2 
        }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
              <CalendarToday color="primary" sx={{ fontSize: '1.4rem' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.15rem' } }}>
                Application Date Visibility
              </Typography>
              <Chip 
                label={showAllDates ? "All Dates Visible" : "All Dates Hidden"} 
                color={showAllDates ? "success" : "warning"}
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 700 }}>
              Turn <strong>ON</strong> to display dates throughout the application and Excel exports. 
              Turn <strong>OFF</strong> to hide all date columns and timestamps from the entire application UI and exported Excel sheets (database records remain safely intact).
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, alignSelf: { xs: 'flex-end', sm: 'center' } }}>
            <Typography variant="body2" fontWeight={600} color={showAllDates ? "primary.main" : "text.secondary"}>
              {showAllDates ? "ENABLED" : "DISABLED"}
            </Typography>
            {updatingSetting ? (
              <CircularProgress size={24} sx={{ mx: 1 }} />
            ) : (
              <Switch
                checked={Boolean(showAllDates)}
                onChange={handleToggleShowAllDates}
                color="primary"
                inputProps={{ 'aria-label': 'Toggle all dates visibility' }}
              />
            )}
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={selectedTab} 
          onChange={(e, newValue) => setSelectedTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {categories.map((category, index) => (
            <Tab 
              key={category.value} 
              label={category.label}
              icon={<Settings />}
            />
          ))}
        </Tabs>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Value</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Order</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getFilteredOptions().map((option) => (
              <TableRow key={option._id}>
                <TableCell>{option.value}</TableCell>
                <TableCell>{option.label}</TableCell>
                <TableCell>
                  <Chip 
                    label={categories.find(c => c.value === option.category)?.label}
                    color={getCategoryColor(option.category)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{option.order}</TableCell>
                <TableCell>
                  <Switch
                    checked={option.active}
                    onChange={() => handleToggleActive(option._id, option.active)}
                    color="primary"
                  />
                </TableCell>
                <TableCell>
                  <IconButton 
                    size="small" 
                    onClick={() => handleEdit(option)}
                    color="primary"
                  >
                    <Edit />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>{editOption ? 'Edit Option' : 'Add New Option'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  name="category"
                  label="Category"
                  select
                  value={formData.category}
                  onChange={handleChange}
                  fullWidth
                  required
                >
                  {categories.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="value"
                  label="Value"
                  value={formData.value}
                  onChange={handleChange}
                  fullWidth
                  required
                  helperText="Internal value used in code"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="label"
                  label="Display Label"
                  value={formData.label}
                  onChange={handleChange}
                  fullWidth
                  required
                  helperText="Text shown to users"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="order"
                  label="Display Order"
                  type="number"
                  value={formData.order}
                  onChange={handleChange}
                  fullWidth
                  helperText="Lower numbers appear first"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? (editOption ? 'Updating...' : 'Creating...') : (editOption ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default DropdownManagement;